// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { DEFAULT_UNIT_OF_MEASURE } from '@/constants/unit-of-measure';
import { SparseItem } from '@/sparse/api/types';

/**
 * Input type for extractSparseItem - supports both:
 * - Objects with nested `item` field (new structure: order items, shipment items)
 * - Objects with flattened SparseItem fields (legacy: ProjectAssetAssignment)
 *
 * unitOfMeasure is optional to support types like ItemDetail that don't include it.
 */
type FlattenedSparseItemInput = Omit<SparseItem, 'unitOfMeasure'> & {
  unitOfMeasure?: SparseItem['unitOfMeasure'];
};

interface NestedSparseItemInput {
  item: SparseItem;
}

type ExtractSparseItemInput = FlattenedSparseItemInput | NestedSparseItemInput;

/**
 * Type guard to check if input has nested item structure
 */
function hasNestedItem(
  input: ExtractSparseItemInput
): input is NestedSparseItemInput {
  return 'item' in input;
}

/**
 * Extracts a SparseItem from an object that either:
 * - Contains a nested `item` field (new structure for order items)
 * - Has SparseItem fields at the top level (legacy flattened structure)
 *
 * @param input - An object with either nested item or flattened SparseItem fields
 * @returns A SparseItem with the base item properties
 */
export function extractSparseItem(input: ExtractSparseItemInput): SparseItem {
  if (hasNestedItem(input)) {
    return input.item;
  }

  // Flattened structure (legacy types like ProjectAssetAssignment)
  return {
    id: input.id,
    key: input.key,
    name: input.name,
    type: input.type,
    sku: input.sku,
    isArchived: input.isArchived,
    unitOfMeasure: input.unitOfMeasure ?? DEFAULT_UNIT_OF_MEASURE,
    hasAssets: input.hasAssets,
    isPhysical: input.isPhysical,
    shouldRenderSerialFields: input.shouldRenderSerialFields,
  };
}
